import _ from "lodash";
import { graphql } from "lib/apolloClient";
import {
  CACHE_AND_NETWORK,
  STUDENT_USER,
  FAMILY_MEMBER,
  GRADE,
  COURSE,
} from "Constants/stringConstants";
import {
  getUserRnpRolesQuery,
  getCalendarEventsQuery,
  getUserProjectGroupYearGroupsQuery,
  getCalendarUidQuery,
  getCalendarEventsDataQuery,
  getCalendarEventDetailsQuery,
  getCalendarEventsParticipantDataQuery,
  getCalendarEventAssociatedEntitiesQuery,
} from "MyCalendar/modules/MyCalendarQuery";
import {
  canFamilyViewDeadline,
  isNewParticipantSelectorEnabled,
  isBigOrgFFEnabledAndStaffUser,
} from "MyCalendar/modules/Utils";
import {
  getCalendarEventsFromCache,
  getUserProjectGroupYearGroupsFromCache,
  getCalendarUidFromCache,
  getCalendarEventDetailsFromCache,
} from "MyCalendar/modules/MyCalendarGraphqlHelpers";
import { getCalendarEventTagsQuery } from "SchoolCalendar/modules/CalendarQuery";
import { getCalendarEventTagsFromCache } from "SchoolCalendar/modules/CalendarGraphqlHelper";
import {
  TYPENAME_MAPPING,
  ENTITY_TYPE,
  ENTITY_TYPE_MAPPING,
} from "MyCalendar/components/EventModalV2/modules/Utils";

const getUserId = ({ isUserParent, childId, userId }) => {
  return canFamilyViewDeadline(isUserParent) ? childId : userId;
};

const getUserEntityType = ({ isUserParent, userEntityType }) => {
  return canFamilyViewDeadline(isUserParent) ? "STUDENT" : userEntityType;
};

export const getUserProjectGroupYearGroupsEnhancer = ({
  selector,
  fetchAll = false,
}) =>
  graphql(getUserProjectGroupYearGroupsQuery, {
    name: "getUserProjectGroupYearGroups",
    skip: ({ isProjectGroupsQueriesSkip }) => isProjectGroupsQueriesSkip,
    options: ({
      userId,
      userEntityType,
      curriculumProgramIds,
      academicYearId,
      projectGroupSubTypes,
      childId,
      isUserParent,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: getUserId({ isUserParent, childId, userId }),
        type: getUserEntityType({
          isUserParent,
          userEntityType,
        }),
        filters: {
          curriculumProgramIds,
          fetchAll,
          projectGroupSubTypes,
        },
        gradeFilters: {
          academicYearId,
        },
      },
    }),
    props({
      getUserProjectGroupYearGroups,
      ownProps: {
        userId,
        userEntityType,
        curriculumProgramIds,
        academicYearId,
        isData,
        isLoading,
        projectGroupSubTypes,
        childId,
        isUserParent,
      },
    }) {
      const data = getUserProjectGroupYearGroupsFromCache({
        id: getUserId({ isUserParent, childId, userId }),
        type: getUserEntityType({
          isUserParent,
          userEntityType,
        }),
        filters: {
          curriculumProgramIds,
          fetchAll,
          projectGroupSubTypes,
        },
        gradeFilters: {
          academicYearId,
        },
      });

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getUserProjectGroupYearGroups["networkStatus"]) ||
          isLoading,
        ...selector({ queryData: data }),
      };
    },
  });

export const getCalendarEventsEnhancer = ({ selector }) => {
  return graphql(getCalendarEventsQuery, {
    name: "getCalendarEvents",
    options: ({
      userId,
      userEntityType,
      filters,
      statusWiseProjectFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        filters,
        statusWiseProjectFilters,
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getCalendarEvents,
      ownProps: { userId, userEntityType, filters, statusWiseProjectFilters },
    }) {
      const queryData = getCalendarEventsFromCache({
        id: userId,
        type: userEntityType,
        filters: filters,
        statusWiseProjectFilters,
      });
      const refetchUpcomingEvents = getCalendarEvents.refetch;

      return {
        isData: !_.isEmpty(queryData),
        isLoading:
          getCalendarEvents["networkStatus"] == 1 ||
          getCalendarEvents["networkStatus"] == 2,
        refetchUpcomingEvents,
        onPusherEventTriggered: () => {
          refetchUpcomingEvents();
        },
        ...selector({ queryData }),
      };
    },
  });
};

export const getCalendarEventTagsEnhancer = () =>
  graphql(getCalendarEventTagsQuery, {
    name: "getCalendarEventTags",
    skip: ({ isMasterCalendarEnabled }) => !isMasterCalendarEnabled,
    options: ({ organizationId }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: organizationId,
      },
    }),
    props({
      getCalendarEventTags,
      ownProps: { organizationId, isData, isLoading },
    }) {
      const data = getCalendarEventTagsFromCache({
        id: organizationId,
      });
      const schoolCalendarEventTags = _.get(data, "calendarEventTags", []);
      return {
        schoolCalendarEventTags,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getCalendarEventTags["networkStatus"]) ||
          isLoading,
      };
    },
  });

export const getCalendarUidEnhancer = () =>
  graphql(getCalendarUidQuery, {
    name: "getCalendarUid",
    options: ({ userId, userEntityType, studentId }) => {
      return {
        fetchPolicy: CACHE_AND_NETWORK,
        variables: {
          id: userId,
          type: userEntityType,
          studentId,
        },
      };
    },
    props({
      getCalendarUid,
      ownProps: { userId, userEntityType, isData, isLoading, studentId },
    }) {
      const data = getCalendarUidFromCache({
        id: userId,
        type: userEntityType,
        studentId,
      });
      const calendarUids = _.get(data, "calendars", []);
      return {
        calendarUids,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getCalendarUid["networkStatus"]) || isLoading,
      };
    },
  });

export const getCalendarEventsDataEnhancer = () => {
  return graphql(getCalendarEventsDataQuery, {
    name: "getCalendarEvents",
    skip: ({ filters, isCalendarDeadlineView }) =>
      !isCalendarDeadlineView &&
      _.isEmpty(_.get(filters, "calendarIds")) &&
      _.isEmpty(_.get(filters, "projectGroupIds")),
    options: ({
      userId,
      userEntityType,
      filters,
      selectedChildId,
      isUserParent,
      statusWiseProjectFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: isUserParent ? selectedChildId : userId,
        type: isUserParent ? STUDENT_USER : userEntityType,
        filters: {
          ...filters,
          calendarIds: _.uniq(filters?.calendarIds || []),
        },
        statusWiseProjectFilters,
      },
    }),
    props({
      getCalendarEvents,
      ownProps: { isData, userEntityType, isLoading, courseId },
    }) {
      const { filters } = getCalendarEvents.variables;
      const queryData = _.get(getCalendarEvents, "node", {});
      const refetchEvents = getCalendarEvents.refetch;
      const calendarEvents = !_.isEmpty(filters?.contentTypes)
        ? _.get(queryData, "calendarEvents", [])
        : [];

      const updatedCalendarEvents = _.map(calendarEvents, event => {
        const { item } = event;
        if (
          _.includes([FAMILY_MEMBER, STUDENT_USER], userEntityType) &&
          event?.item?.__typename === "ProjectTaskResponse"
        ) {
          return {
            ...event,
            item: {
              ...item,
              deadline: item?.task,
            },
          };
        }
        return event;
      });

      return {
        onPusherEventTriggered: params => {
          if (courseId) {
            if (params.courseId === courseId) {
              refetchEvents();
            }
          } else {
            refetchEvents();
          }
        },
        isData: !_.isEmpty(queryData) && isData,
        calendarEvents: updatedCalendarEvents,
        refetchEvents,
        isLoading:
          getCalendarEvents["networkStatus"] == 1 ||
          getCalendarEvents["networkStatus"] == 2 ||
          isLoading,
      };
    },
  });
};

export const getCalendarEventDetailsEnhancer = () => {
  return graphql(getCalendarEventDetailsQuery, {
    name: "getCalendarEventDetails",
    options: ({
      eventId,
      entityType = "PLATFORM_CALENDAR_EVENT",
      statusWiseProjectFilters = {},
      calendarUserType,
      organizationId,
      skipAssociatedEntities = false,
    }) => {
      const variables = {
        id: eventId,
        type: entityType,
        calendarUserType,
        associatedEntitiesFilters: {
          entityTypes: ["GRADE", "COURSE"],
        },
        organizationId,
        skipAssociatedEntities,
        skipUserDetails:
          isNewParticipantSelectorEnabled() && isBigOrgFFEnabledAndStaffUser(),
      };
      if (!_.isEmpty(statusWiseProjectFilters)) {
        variables.statusWiseProjectFilters = statusWiseProjectFilters;
      }
      return {
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        variables,
      };
    },
    props({
      getCalendarEventDetails,
      ownProps: {
        eventId,
        userEntityType,
        isData,
        isLoading,
        userId,
        isUserParent,
        selectedChildId,
        filters,
        skipAssociatedEntities,
        isAgendaView,
        isStaffUser,
      },
    }) {
      const { networkStatus } = getCalendarEventDetails;

      // Try to read from cache first using the same variables as useCalendarEvents
      const cachedData = getCalendarEventDetailsFromCache({
        eventId,
        userId,
        userEntityType,
        filters,
        isUserParent,
        selectedChildId,
        eventDetailsVariables: getCalendarEventDetails.variables,
        isAgendaView,
        isUserStaff: isStaffUser,
      });

      // Get data from query or use cached data
      const queryEventData = _.get(getCalendarEventDetails, "node", {});
      const eventData = _.cloneDeep(
        !_.isEmpty(queryEventData) ? queryEventData : cachedData
      );

      const { item } = eventData;
      if (!_.isEmpty(eventData)) {
        // don't remove this check because if eventData is empty then set will set the value to eventData.item to "" and
        // on checking _.isEmpty(eventData) it will return false
        _.set(eventData, "item.category", _.get(item, "tag.label", ""));
      }

      if (
        item?.__typename === "ProjectTaskResponse" &&
        _.includes([FAMILY_MEMBER, STUDENT_USER], userEntityType)
      ) {
        eventData.item.deadline = item?.task;
        eventData.item.project = item?.mappedProject;
      }

      // Only show loading skeleton on initial load (networkStatus 1 & 2)
      const loading = _.includes([1, 2], networkStatus) || isLoading;

      // Detect background refresh (networkStatus 3)
      const isFetchingMore = networkStatus === 3;

      const result = {
        isData: !_.isEmpty(eventData) && isData,
        eventData,
        isLoading: loading,
        isEventDetailsLoading: loading && _.isEmpty(queryEventData),
        isFetchingMore,
        networkStatus,
      };

      // When isLazyLoadEnabled is false, associatedEntities (grades/courses) are
      // already included in this query (skipAssociatedEntities = false for staff).
      // Derive and expose them here so the separate associated-entities query is not needed.
      const isLazyLoadEnabled =
        isNewParticipantSelectorEnabled() && isBigOrgFFEnabledAndStaffUser();
      if (!isLazyLoadEnabled && !skipAssociatedEntities) {
        const entities = _.get(eventData, ["item", "associatedEntities"], []);
        const typeName = _.get(entities, "0.__typename", "");
        result.associatedEntities = entities;
        result.participantListType =
          TYPENAME_MAPPING[typeName] || ENTITY_TYPE_MAPPING.CUSTOM;
        result.isAssociatedEntitiesDetailsLoading =
          loading && _.isEmpty(queryEventData);
      }

      return result;
    },
  });
};

export const getCalendarEventsParticipantDataEnhancer = () => {
  return graphql(getCalendarEventsParticipantDataQuery, {
    name: "getCalendarEventsParticipantData",
    skip: ({
      filters,
      isAgendaView,
      showParticipantOnAgendaAndViewmore,
      isUserStaff,
    }) =>
      !isUserStaff ||
      !showParticipantOnAgendaAndViewmore ||
      !isAgendaView ||
      (_.isEmpty(_.get(filters, "calendarIds")) &&
        _.isEmpty(_.get(filters, "projectGroupIds"))),
    options: ({
      userId,
      userEntityType,
      filters,
      statusWiseProjectFilters,
      selectedChildId,
      isUserParent,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: isUserParent ? selectedChildId : userId,
        type: isUserParent ? STUDENT_USER : userEntityType,
        filters: {
          ...filters,
          calendarIds: _.uniq(filters?.calendarIds || []),
        },
        statusWiseProjectFilters,
        associatedEntitiesFilters: {
          entityTypes: [GRADE, COURSE],
        },
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getCalendarEventsParticipantData }) {
      const queryData = _.get(getCalendarEventsParticipantData, "node", {});
      const calendarEventsParticipantData = _.get(
        queryData,
        "calendarEvents",
        []
      );
      const refetchAssignedData = getCalendarEventsParticipantData.refetch;

      return {
        calendarEventsParticipantData,
        isCalendarEventsParticipantDataLoading:
          getCalendarEventsParticipantData["networkStatus"] == 1 ||
          getCalendarEventsParticipantData["networkStatus"] == 2,
        refetchAssignedData,
      };
    },
  });
};

/**
 * GraphQL enhancer for fetching calendar event associated entities (grades/courses).
 * Used to get participant information for video meeting events.
 *
 * Expected props from mapStateToProps or parent component:
 * @param {boolean} isVideoMeetingRevampEnabled - Feature flag to enable video meeting revamp functionality
 * @param {string} eventId - ID of the calendar event to fetch associated entities for
 * @param {Object} eventData - Calendar event data object containing item details
 * @param {string} userType - Type of user ("staff", "student", "parent")
 * @param {string} [entityType="VIDEO_MEETING"] - Entity type for the query (defaults to "VIDEO_MEETING")
 * @param {boolean} [isLoading=false] - Loading state from parent component
 * @param {boolean} [isData=true] - Data availability state from parent component
 *
 * @returns {Function} GraphQL HOC that provides:
 * @returns {Array} associatedEntities - Array of associated Grade or Course entities with participant counts
 * @returns {string} participantListType - Type of participant list ("GRADE", "COURSE", or "CUSTOM")
 * @returns {boolean} isAssociatedEntitiesDetailsLoading - Loading state indicator
 * @returns {boolean} isData - Whether data is available in cache
 */
export const getCalendarEventAssociatedEntitiesEnhancer = () => {
  return graphql(getCalendarEventAssociatedEntitiesQuery, {
    name: "getCalendarEventAssociatedEntities",
    skip: ({ isVideoMeetingRevampEnabled, eventId, eventData, userType }) => {
      if (!eventId) return true;
      const associatedEntities = _.get(
        eventData,
        ["item", "associatedEntities"],
        []
      );
      const eventType = _.get(eventData, "item.eventType", "");
      const typeName = _.get(associatedEntities, "0.__typename", "");
      const participantListType =
        TYPENAME_MAPPING[typeName] || ENTITY_TYPE.CUSTOM;
      return (
        participantListType === ENTITY_TYPE.CUSTOM ||
        eventType !== "VIDEO_MEETING" ||
        userType !== "staff" ||
        !isVideoMeetingRevampEnabled ||
        isNewParticipantSelectorEnabled() ||
        isBigOrgFFEnabledAndStaffUser()
      );
    },
    options: ({ eventId, entityType = "VIDEO_MEETING" }) => ({
      variables: {
        id: eventId,
        type: entityType,
        associatedEntitiesFilters: {
          entityTypes: [GRADE, COURSE],
        },
      },
    }),
    props({ getCalendarEventAssociatedEntities, ownProps: { isLoading } }) {
      const associatedEntities = _.get(
        getCalendarEventAssociatedEntities,
        "node.item.associatedEntities",
        []
      );

      const typeName = _.get(associatedEntities, "0.__typename", "");
      const participantListType = TYPENAME_MAPPING[typeName] || "USER";
      const { networkStatus } = getCalendarEventAssociatedEntities;

      return {
        associatedEntities,
        participantListType,
        isAssociatedEntitiesDetailsLoading:
          _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  });
};

export const getUserRnPAccountRoles = () => {
  // Fetch the current user's RnP roles to determine super admin status.
  // Skipped when VideoMeetingAdminEdit flag is off since the check is not needed.
  return graphql(getUserRnpRolesQuery, {
    name: "getUserRnpRoles",
    options: ({ userId, userType, userRnpRolesFilter }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: userId,
        type: _.upperCase(userType),
        filters: userRnpRolesFilter,
      },
    }),
    skip: ({ skipGetUserRnpRolesQuery }) => skipGetUserRnpRolesQuery,
    props({ getUserRnpRoles, ownProps: { isData, isLoading } }) {
      const rnpRolesData = _.get(getUserRnpRoles, "node", {});
      return {
        isData: !_.isEmpty(rnpRolesData) && isData,
        isLoading:
          _.includes([1, 2], getUserRnpRoles["networkStatus"]) || isLoading,
        rnpRoleContextDetail: _.get(rnpRolesData, "rnpRoleContextDetail"),
      };
    },
  });
};
